
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ConsultModal from '../../components/feature/ConsultModal';
import ScrollToTop from '../../components/feature/ScrollToTop';
import SeoHead from '../../components/feature/SeoHead';
import ExportHeroSection from './components/ExportHeroSection';
import OpportunitySection from './components/OpportunitySection';
import ExportServicesSection from './components/ExportServicesSection';
import AsianAlleySection from './components/AsianAlleySection';
import ExportProcessSection from './components/ExportProcessSection';
import WhyUsSection from './components/WhyUsSection';
import ExportContactSection from './components/ExportContactSection';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://thedaeg.com';

const seoData: Record<string, { title: string; description: string; keywords: string; path: string }> = {
  en: {
    title: 'The Daeg International Trade - Export Asian Goods to Baltics & Northern Europe via Latvia',
    description:
      'The Daeg International Trade - Trusted partner for exporting Asian goods to Baltics and Northern Europe via Latvia. Asian Alley distribution system, 5+ years experience, professional logistics, competitive costs. Gateway to 30 million population market.',
    keywords:
      'Asian export Latvia, Baltics import export, Northern Europe distribution, Asian goods EU, The Daeg International, Asian Alley Riga, Latvia logistics',
    path: '/en',
  },
  lv: {
    title: 'The Daeg International Trade - Āzijas preču eksports uz Baltiju un Ziemeļeiropu caur Latviju',
    description:
      'The Daeg International Trade - uzticams partneris Āzijas preču eksportam uz Baltijas un Ziemeļeiropas tirgu caur Latviju. Asian Alley izplatīšanas sistēma, 5+ gadu pieredze.',
    keywords:
      'Āzijas eksports Latvija, Baltijas imports, Ziemeļeiropa, Asian Alley Rīga, loģistika Latvija',
    path: '/lv',
  },
  vi: {
    title: 'The Daeg International Trade - Xuất Nhập Khẩu Hàng Hóa Châu Á sang Baltics & Bắc Âu qua Latvia',
    description:
      'The Daeg International Trade - Đối tác xuất khẩu hàng hóa Châu Á sang thị trường Baltics và Bắc Âu qua Latvia. Hệ thống phân phối Asian Alley, 5+ năm kinh nghiệm, logistics chuyên nghiệp, chi phí cạnh tranh. Cửa ngõ vàng vào thị trường 30 triệu dân Baltics & Bắc Âu.',
    keywords:
      'xuất khẩu châu Á, nhập khẩu Latvia, xuất khẩu Baltics, xuất khẩu Bắc Âu, phân phối hàng Châu Á, The Daeg International, Asian Alley, logistics Latvia',
    path: '/vi',
  },
};

const HomePage = () => {
  const { i18n } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const lang = (i18n.language in seoData ? i18n.language : 'vi') as keyof typeof seoData;
  const seo = seoData[lang];

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'The Daeg International Trade',
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/vi/dinh-cu-latvia/blog?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'The Daeg International Trade',
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    description:
      'Công ty xuất nhập khẩu hàng hóa Châu Á tại Latvia, sở hữu chuỗi siêu thị Asian Alley. Cung cấp dịch vụ xuất khẩu sang Baltics & Bắc Âu và đầu tư định cư Latvia.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Krisjana Barona Iela 96/98',
      addressLocality: 'Riga',
      addressCountry: 'LV',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+371-2572-6969',
      contactType: 'customer service',
      availableLanguage: ['Vietnamese', 'English', 'Latvian'],
    },
    sameAs: [
      'https://www.facebook.com/thedaeg',
      'https://www.linkedin.com/company/thedaeg',
    ],
  };

  return (
    <div className="min-h-screen bg-black">
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath={seo.path}
        schemaJson={[websiteSchema, orgSchema]}
      />
      <header>
        <Navbar onConsultClick={() => setIsModalOpen(true)} />
      </header>

      <main>
        <h1 className="sr-only">
          The Daeg International Trade - Xuất Nhập Khẩu Hàng Hóa Châu Á tại Latvia, Baltics & Bắc Âu
        </h1>

        <ExportHeroSection onContactClick={() => setIsModalOpen(true)} />
        <OpportunitySection />
        <ExportServicesSection />
        <AsianAlleySection />
        <ExportProcessSection />
        <WhyUsSection />
        <ExportContactSection />
      </main>

      <Footer />
      <ConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <ScrollToTop />
    </div>
  );
};

export default HomePage;
